import type { ClientIntent, TableSnapshot } from '@tctp/protocol';
import { useEffect, useMemo, useRef, useState } from 'react';
import { cardView } from '../cardView';
import { useShortcuts } from '../useShortcuts';
import { Modal } from './Modal';
import { RaisePanel } from './RaisePanel';

const SKIP_FOLD_QUESTION = 'tctp:skipFoldQuestion';   // sessionStorage: the player ticked "don't ask again this session"
const foldQuestionSkipped = (): boolean => { try { return sessionStorage.getItem(SKIP_FOLD_QUESTION) === '1'; } catch { return false; } };
const skipFoldQuestion = (): void => { try { sessionStorage.setItem(SKIP_FOLD_QUESTION, '1'); } catch { /* private mode: ask every time */ } };

function ActButton({ hint, label, danger = false, disabled = false, onClick }: { hint: string; label: string; danger?: boolean; disabled?: boolean; onClick: () => void }) {
  return (
    <button type="button" className={`act-btn${danger ? ' danger' : ''}`} disabled={disabled} onClick={onClick}>
      <span className="hint" aria-hidden="true">{hint}</span>{label}
    </button>
  );
}

/** Bottom-right of the stage: lobby controls, the betting buttons, the raise flow, or the discard flow (spec §6). */
export function ActionArea({ snapshot, act, discardRemainingMs, hasPick, cardsLanded = true, onDiscard, onTopUp, shortcutsEnabled, finished }: {
  snapshot: TableSnapshot; act: (intent: ClientIntent) => Promise<void>; discardRemainingMs: number | null;
  hasPick: boolean; cardsLanded?: boolean; onDiscard: () => void; onTopUp: () => void; shortcutsEnabled: boolean;   // `cardsLanded`: the viewer's three cards are face-up; until then the discard panel waits
  /** The hand is over and its reveal has landed (TableView's paced `shownShowdown`), so the show row appears with the result, not before. */
  finished: boolean;
}) {
  const [raising, setRaising] = useState(false);
  const [askingFold, setAskingFold] = useState(false);   // "Fold anyway?": a fold that could have been a check (PokerNow's unnecessary-fold question)
  const [dontAskAgain, setDontAskAgain] = useState(false);
  const [checkOrFold, setCheckOrFold] = useState(false);   // pre-action while waiting: check if free when the turn comes, else fold (PokerNow's CHECK OR FOLD, key I)
  const { you, hand, legalActions: legal } = snapshot;
  const me = you.seat === null ? null : snapshot.seats[you.seat] ?? null;
  const picker = hand?.phase === 'discard' && me !== null && me.inHand && !me.folded ? me : null;
  const betting = legal !== null && hand !== null && hand.phase !== 'complete';
  const showRaise = raising && betting && legal.minRaiseTo !== null;

  // One action in flight at a time: a second tap or key press before the server has answered is ignored, so it cannot be refused as stale.
  const inFlight = useRef(false);   // a ref, so the memoised keyboard shortcuts see it too
  const [pending, setPending] = useState(false);
  const send = (intent: ClientIntent): void => {
    if (inFlight.current) return;
    inFlight.current = true;
    setPending(true);
    setRaising(false);
    void act(intent).finally(() => { inFlight.current = false; setPending(false); });
  };
  const minBet = betting && legal.callAmount === null && legal.minRaiseTo !== null && hand.currentBet === 0 ? legal.minRaiseTo : null;
  const fold = (): void => { if (betting && legal.canCheck && !foldQuestionSkipped()) { setDontAskAgain(false); setAskingFold(true); } else send({ type: 'fold' }); };
  const confirmFold = (): void => { if (dontAskAgain) skipFoldQuestion(); setAskingFold(false); send({ type: 'fold' }); };
  // Run it twice: while the hand waits for the vote, an asked seat that has not answered sees YES / NO; an answered one waits.
  const vote = hand?.runItTwice ?? null;
  const myVote = vote !== null && me !== null && vote.asked.includes(me.seat) ? (vote.answered.includes(me.seat) ? 'answered' : 'asked') : null;
  // The pre-action is offered whenever the viewer is in a live hand and not the one to act: waiting for the others' discards, or for the action to reach them.
  const waiting = vote === null && hand !== null && me !== null && me.inHand && !me.folded && !me.allIn && legal === null && hand.phase !== 'showdown' && hand.phase !== 'complete' && (picker === null || picker.discardLocked);
  useEffect(() => { setCheckOrFold(false); }, [hand?.handId]);
  useEffect(() => {
    if (!checkOrFold || !betting || pending) return;
    setCheckOrFold(false);   // it fires once: the next street starts unarmed
    send({ type: legal.canCheck ? 'check' : 'fold' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkOrFold, betting, pending, legal?.canCheck]);
  const canDiscard = picker !== null && !picker.discardLocked && hasPick && cardsLanded;
  // After the hand, a player whose kept cards are not both public may show them all or one at a time (spec: showdown mucking).
  const unshown = finished && me !== null && me.inHand && me.keepers !== null ? me.keepers.filter(c => !me.shown.includes(c)) : [];
  // Rabbit hunting: after an early finish, any seated player may ask to see the undealt board; it shows on their screen only.
  const rabbit = finished && me !== null && hand !== null && hand.rabbitHunt && hand.phase === 'complete' && hand.board.length < 5 && hand.rabbit === null;

  const shortcuts = useMemo(() => ({
    s: unshown.length > 0 ? () => send({ type: 'showCards' }) : undefined,
    1: unshown[0] !== undefined ? () => send({ type: 'showCards', cards: [unshown[0]!] }) : undefined,
    2: unshown[1] !== undefined ? () => send({ type: 'showCards', cards: [unshown[1]!] }) : undefined,
    f: betting && legal.canFold && !showRaise ? fold : undefined,
    i: waiting ? () => setCheckOrFold(v => !v) : undefined,
    y: myVote === 'asked' ? () => send({ type: 'runItTwice', yes: true }) : undefined,
    n: myVote === 'asked' ? () => send({ type: 'runItTwice', yes: false }) : undefined,
    k: betting && legal.canCheck && !showRaise ? () => send({ type: 'check' }) : undefined,
    c: betting && !showRaise ? (legal.callAmount !== null ? () => send({ type: 'call' }) : minBet !== null ? () => send({ type: 'raise', to: minBet }) : undefined) : undefined,
    r: betting && legal.minRaiseTo !== null && !showRaise ? () => setRaising(true) : undefined,
    Escape: showRaise ? () => setRaising(false) : undefined,
    Enter: canDiscard ? onDiscard : undefined,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [betting, legal, showRaise, minBet, canDiscard, onDiscard, unshown.join(), waiting, myVote]);
  useShortcuts(shortcuts, shortcutsEnabled && !askingFold);
  useShortcuts(useMemo(() => ({ Enter: askingFold ? confirmFold : undefined }), [askingFold, dontAskAgain]), shortcutsEnabled && askingFold);   // eslint-disable-line react-hooks/exhaustive-deps

  const foldQuestion = askingFold && (
    <Modal title="Fold anyway?" onClose={() => setAskingFold(false)}>
      <p className="modal-text">You can check for free. Folding now gives up the hand for nothing.</p>
      <label className="check"><input type="checkbox" checked={dontAskAgain} onChange={e => setDontAskAgain(e.target.checked)} />Don't ask again this session</label>
      <div className="modal-actions">
        <button type="button" className="btn grey" onClick={() => setAskingFold(false)}>CANCEL</button>
        <button type="button" className="btn red" onClick={confirmFold}>CONFIRM</button>
      </div>
    </Modal>
  );
  const preAction = waiting && (
    <div className="act-row">
      <button type="button" className={`act-btn pre${checkOrFold ? ' on' : ''}`} aria-pressed={checkOrFold} onClick={() => setCheckOrFold(v => !v)}><span className="hint" aria-hidden="true">I</span>CHECK OR FOLD</button>
    </div>
  );

  const showRow = (unshown.length > 0 || rabbit) && (
    <div className="show-row">
      {unshown.length > 0 && <button type="button" className="btn green" disabled={pending} onClick={() => send({ type: 'showCards' })}><span className="hint" aria-hidden="true">S</span>SHOW ALL CARDS</button>}
      {unshown.map((c, i) => <ActButton key={c} hint={String(i + 1)} label={`${cardView(c).rank}${cardView(c).glyph}`} disabled={pending} onClick={() => send({ type: 'showCards', cards: [c] })} />)}
      {rabbit && <button type="button" className="btn grey" disabled={pending} onClick={() => send({ type: 'rabbitHunt' })}>RABBIT HUNT</button>}
    </div>
  );

  if (snapshot.ended) return <div className="action-area"><div className="action-label">SESSION ENDED</div></div>;
  if (snapshot.paused) {
    return (
      <div className="action-area">
        <div className="action-label">PAUSED</div>
        {you.isHost && <button type="button" className="btn green big" disabled={pending} onClick={() => send({ type: 'resume' })}>RESUME</button>}
        {showRow}
      </div>
    );
  }
  const seatedCount = snapshot.seats.filter(s => s !== null).length;
  if (!snapshot.running) {
    return <div className="action-area">{you.isHost && <button type="button" className="btn green big" disabled={seatedCount < 2 || pending} title={seatedCount < 2 ? 'Two players are needed' : undefined} onClick={() => send({ type: 'startGame' })}>START GAME</button>}</div>;
  }
  const idle = hand === null || hand.phase === 'complete';
  // Sitting out: between hands, or while a hand is played without them (a hand they sat out during keeps its usual controls until it ends).
  if (me !== null && me.away && (idle || !me.inHand)) {
    return (
      <div className="action-area">
        <div className="keep-panel">
          <div className="keep-caption">You're away</div>
          <button type="button" className="btn green big" disabled={pending} onClick={() => send({ type: 'setAway', away: false })}>I'M BACK</button>
        </div>
        {showRow}
      </div>
    );
  }
  if (me !== null && me.busted && (idle || !me.inHand)) {
    return (
      <div className="action-area">
        <div className="keep-panel">
          <div className="keep-caption">You're out of chips</div>
          <button type="button" className="btn green big" onClick={onTopUp}>TOP UP</button>
        </div>
        {showRow}
      </div>
    );
  }
  if (idle && snapshot.seats.filter(s => s !== null && s.stack > 0).length < 2) {
    return <div className="action-area"><div className="action-label">Waiting for another player with chips…</div>{showRow}</div>;
  }
  if (showRow) return <div className="action-area">{showRow}</div>;
  if (picker !== null) {
    if (picker.discardLocked) return <div className="action-area"><div className="action-label">Waiting for others…</div>{preAction}</div>;
    if (!cardsLanded) return <div className="action-area" />;   // the deal is still in the air
    return (
      <div className="action-area">
        <div className="your-turn"><span className="dot" />YOUR TURN</div>
        <div className="keep-panel">
          <div className="keep-caption">Choose one card to discard{discardRemainingMs !== null && <span className="seconds">{Math.ceil(discardRemainingMs / 1000)}s</span>}</div>
          <button type="button" className="btn green big" disabled={!canDiscard} onClick={onDiscard}>DISCARD</button>
        </div>
      </div>
    );
  }
  if (myVote === 'asked') {
    return (
      <div className="action-area">
        <div className="rit-panel">
          <div className="rit-title">Run it twice?</div>
          <div className="act-row">
            <button type="button" className="btn green big" disabled={pending} onClick={() => send({ type: 'runItTwice', yes: true })}><span className="hint" aria-hidden="true">Y</span>YES</button>
            <button type="button" className="btn red big" disabled={pending} onClick={() => send({ type: 'runItTwice', yes: false })}><span className="hint" aria-hidden="true">N</span>NO</button>
          </div>
        </div>
      </div>
    );
  }
  if (myVote === 'answered') return <div className="action-area"><div className="action-label">Waiting for the others…</div></div>;
  if (!betting) return <div className="action-area">{preAction}</div>;
  if (showRaise) {
    return (
      <div className="action-area raising">
        <RaisePanel legal={legal} potTotal={hand.potTotal} currentBet={hand.currentBet} bigBlind={snapshot.config.bigBlind}
          onRaise={to => send({ type: 'raise', to })} onBack={() => setRaising(false)} />
      </div>
    );
  }
  return (
    <div className="action-area">
      <div className="your-turn"><span className="dot" />YOUR TURN{hand.extraTime && <span className="extra-time">EXTRA TIME</span>}</div>
      <div className="act-row">
        {legal.callAmount !== null && <ActButton hint="C" label={`CALL ${legal.callAmount}`} disabled={pending} onClick={() => send({ type: 'call' })} />}
        {minBet !== null && <ActButton hint="C" label={`BET ${minBet}`} disabled={pending} onClick={() => send({ type: 'raise', to: minBet })} />}
        {legal.minRaiseTo !== null && <ActButton hint="R" label={hand.currentBet === 0 ? 'BET' : 'RAISE'} disabled={pending} onClick={() => setRaising(true)} />}
        {legal.canCheck && <ActButton hint="K" label="CHECK" disabled={pending} onClick={() => send({ type: 'check' })} />}
        {legal.canFold && <ActButton hint="F" label="FOLD" danger disabled={pending} onClick={fold} />}
      </div>
      {foldQuestion}
    </div>
  );
}
